import React, { useCallback, useEffect, useState } from "react"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { api } from "@/lib/api"
import { OrganizationWithUsers, UserRole } from "@/lib/types"
import { useAuthStore } from "@/stores/authStore"
import { useOrganizationsStore } from "@/stores/organizationsStore"
import { canManageOrgUsers, useCurrentOrgRole } from "@/lib/useCurrentOrgRole"

const ROLE_HELP: Record<UserRole, string> = {
  admin: "Full access, and can invite and manage users in this organization.",
  user: "Full access to patients, campaigns and APCM operations.",
  staff: "Read-only clinical access. Cannot run APCM operations.",
}

/**
 * The organization's user list, with an invite form for org admins.
 *
 * Inviting used to be superuser-only with no UI outside /admin, so every new
 * clinic login went through Pear by hand. The API now allows an org ADMIN to
 * manage their own organization, and this is the surface for it.
 */
export function OrgUsersPanel() {
  const currentUser = useAuthStore((s) => s.user)
  const role = useCurrentOrgRole()
  const { selectedOrganizationId, organizations } = useOrganizationsStore()
  const selectedOrg = organizations.find((o) => o.id === selectedOrganizationId)

  const [org, setOrg] = useState<OrganizationWithUsers | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [email, setEmail] = useState("")
  const [name, setName] = useState("")
  const [inviteRole, setInviteRole] = useState<UserRole>("user")
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const canManage = canManageOrgUsers(role, !!currentUser?.is_superuser)

  const load = useCallback(async () => {
    if (!selectedOrganizationId) return
    setLoading(true)
    setError(null)
    try {
      setOrg(await api.getOrganization(selectedOrganizationId))
    } catch (e) {
      console.error("Failed to load organization users:", e)
      setError("Could not load users for this organization.")
    } finally {
      setLoading(false)
    }
  }, [selectedOrganizationId])

  useEffect(() => {
    load()
  }, [load])

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedOrganizationId || !email.trim()) return
    setSubmitting(true)
    setError(null)
    setNotice(null)
    try {
      await api.inviteUserToOrganization(selectedOrganizationId, {
        email: email.trim(),
        name: name.trim() || undefined,
        role: inviteRole,
      })
      setNotice(
        `Invited ${email.trim()}. They will get an email with a temporary password.`
      )
      setEmail("")
      setName("")
      setInviteRole("user")
      await load()
    } catch (err: any) {
      console.error("Invite failed:", err)
      setError(
        err?.response?.data?.detail ??
          "Could not send the invitation. Please try again."
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (!selectedOrganizationId) {
    return (
      <Alert>
        <AlertTitle>No organization selected</AlertTitle>
        <AlertDescription>
          Pick an organization to manage its users.
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Users</CardTitle>
          <CardDescription>
            People with access to {selectedOrg?.name || "this organization"}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading users…</p>
          ) : org && org.users.length > 0 ? (
            <div className="divide-y">
              {org.users.map((ou) => (
                <div
                  key={ou.id}
                  className="flex items-center justify-between gap-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                      {ou.user.name || ou.user.email}
                    </p>
                    {ou.user.name ? (
                      <p className="truncate text-sm text-muted-foreground">
                        {ou.user.email}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {ou.user.email === currentUser?.email ? (
                      <span className="text-xs text-muted-foreground">You</span>
                    ) : null}
                    <Badge variant="secondary">{ou.role}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No users yet.</p>
          )}
        </CardContent>
      </Card>

      {canManage ? (
        <Card>
          <CardHeader>
            <CardTitle>Invite a user</CardTitle>
            <CardDescription>
              They get an email with a temporary password and can sign in right
              away.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="invite-email">Email</Label>
                  <Input
                    id="invite-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@practice.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="invite-name">Name (optional)</Label>
                  <Input
                    id="invite-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Doe"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invite-role">Role</Label>
                <Select
                  value={inviteRole}
                  onValueChange={(v) => setInviteRole(v as UserRole)}
                >
                  <SelectTrigger id="invite-role" className="w-full md:w-1/2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {ROLE_HELP[inviteRole]}
                </p>
              </div>

              {error ? (
                <Alert variant="destructive">
                  <AlertTitle>Invite failed</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
              {notice ? (
                <Alert>
                  <AlertTitle>Invitation sent</AlertTitle>
                  <AlertDescription>{notice}</AlertDescription>
                </Alert>
              ) : null}

              <div className="flex justify-end">
                <Button type="submit" disabled={submitting || !email.trim()}>
                  {submitting ? "Sending…" : "Send invite"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Alert>
          <AlertTitle>You cannot invite users</AlertTitle>
          <AlertDescription>
            Only organization admins can add people here. Ask an admin at{" "}
            {selectedOrg?.name || "your organization"}, or contact Pear support.
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}

export default OrgUsersPanel
